import React, { useEffect } from "react";
import { ViewportPortal } from "../shared/ViewportDialog";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { RecIcon } from "../materials/MaterialRecordParts";
import "../materials/materialRecord.css";
import "./timeLogs.css";

/** Search + Filter button (dot while any filter is active). */
export function TimeLogsSearchRow({
  search,
  onSearch,
  filterCount,
  onOpenFilters,
  placeholder = "Search time logs…",
  searchLabel = "Search time logs",
}) {
  return (
    <div className="mat-searchrow">
      <div className="mat-searchbox">
        <RecIcon name="search" />
        <input
          type="search"
          className="mat-search"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={placeholder}
          aria-label={searchLabel}
          autoComplete="off"
        />
      </div>
      <button
        type="button"
        className={`mat-act mat-act--icon tl-filterbtn${filterCount ? " has-filter" : ""}`}
        aria-label={filterCount ? `Filters, ${filterCount} active` : "Filters"}
        data-filter-active={filterCount ? "true" : undefined}
        onClick={onOpenFilters}
      >
        <RecIcon name="filter" />
      </button>
    </div>
  );
}

/** "My Time | Team | Organization" quick scope (only scopes the actor may use). */
export function TimeLogsScopeTabs({ options, value, onChange, ariaLabel = "Time log scope" }) {
  if (options.length < 2) return null;
  return (
    <div className="tl-scope" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={`tl-scope__tab${value === option.value ? " is-on" : ""}`}
          title={option.title || undefined}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** "24 logs · 38h 20m" on the left, "+ New" on the right. */
export function TimeLogsActionBar({
  summary,
  onNew,
  newLabel = "New",
  newAriaLabel = "New Time Log",
}) {
  return (
    <div className="tl-actionbar">
      <span className="tl-actionbar__summary" aria-live="polite" data-testid="time-log-summary">
        {summary}
      </span>
      {onNew ? (
        <HubPrimaryAction onClick={onNew} label={newLabel} ariaLabel={newAriaLabel} />
      ) : null}
    </div>
  );
}

/** Removable chips for every active filter, plus Clear all. */
export function TimeLogsActiveFilters({ chips, onClearAll }) {
  if (!chips.length) return null;
  return (
    <div className="mat-chips tl-chips" data-testid="time-log-active-filters">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className="mat-chip"
          onClick={chip.onClear}
          aria-label={`Clear ${chip.label} filter`}
        >
          <span>{chip.label}</span>
          <RecIcon name="close" />
        </button>
      ))}
      {chips.length > 1 ? (
        <button type="button" className="tl-chips__clear" onClick={onClearAll}>
          Clear all
        </button>
      ) : null}
    </div>
  );
}

/**
 * Filter sheet. Native selects (long names truncate, OS pickers on phones).
 * Filters apply immediately; Done just closes.
 */
export function TimeLogsFilterSheet({
  fields,
  onClearAll,
  hasActive,
  onClose,
  title = "Filter time logs",
}) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <ViewportPortal>
    <div
      className="rec-sheet-overlay"
      onClick={onClose}
      role="presentation"
      data-testid="time-log-filter-sheet"
    >
      <div
        className="rec-sheet tl-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rec-sheet__handle" aria-hidden="true" />
        <div className="tl-sheet__head">
          <span className="rec-sheet__title">{title}</span>
          {hasActive ? (
            <button type="button" className="tl-sheet__clear" onClick={onClearAll}>
              Clear all
            </button>
          ) : null}
        </div>
        <div className="tl-sheet__fields">
          {fields.map((field) => (
            <label key={field.key} className="rec-field tl-sheet__field">
              <span className="rec-field__label">{field.label}</span>
              <span className="rec-select">
                <select
                  className="rec-input"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  aria-label={field.label}
                >
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          ))}
        </div>
        <button type="button" className="rec-btn rec-btn--primary tl-sheet__done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
    </ViewportPortal>
  );
}
